import { useCallback, useEffect, useRef, useState } from 'react';
import { useEvent } from './useEvent';

export const useOffsetPagination = ({
  controller = {},
  total,
  pageSize = 10,
  resetToFirstPage = false,
}) => {
  const hasTotal = typeof total === 'number';

  const _total = hasTotal ? total : 0;

  const { value, defaultValue = 1, onChange = noop } = controller;

  const isControlledRef = useRef(Object.hasOwn(controller, 'value'));

  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const [page, setPage] = useState(() =>
    getValidValue(isControlledRef.current ? value : defaultValue)
  );

  const maxPageCount = Math.ceil(_total / pageSize);
  const pageCount = Math.max(1, maxPageCount);

  const isFirstPage = page === 1;
  const isLastPage = page === pageCount;

  const _value = isControlledRef.current ? getValidValue(value) : page;

  const baseSet = useEvent((value) => {
    onChangeRef.current(value);

    if (!isControlledRef.current) {
      setPage(value);
    }
  });

  const set = useEvent((value) => {
    let nextPage = typeof value === 'function' ? value(_value) : value;

    if (nextPage == null || nextPage < 1) {
      nextPage = 1;
    } else if (nextPage > pageCount) {
      nextPage = pageCount;
    }

    baseSet(nextPage);
  });

  const next = useEvent(() => {
    if (isLastPage) {
      return onChangeRef.current(_value);
    }

    baseSet(_value + 1);
  });

  const prev = useEvent(() => {
    if (isFirstPage) {
      return onChangeRef.current(_value);
    }

    baseSet(_value - 1);
  });

  const getDisplayItems = useCallback(
    (data) => {
      if (!Array.isArray(data)) {
        return data;
      }

      const start = (_value - 1) * pageSize;
      const result = data.slice(start, start + pageSize);

      return result;
    },
    [_value, pageSize]
  );

  useEffect(() => {
    if (hasTotal && _value > maxPageCount) {
      baseSet(getValidValue(resetToFirstPage ? 1 : maxPageCount));
    }
  }, [maxPageCount, resetToFirstPage, _value, baseSet, hasTotal]);

  return {
    page,
    setPage: set,
    pageCount,
    next,
    prev,
    isFirstPage,
    isLastPage,
    getDisplayItems,
  };
};

function noop() {}

function getValidValue(value) {
  return value >= 1 ? value : 1;
}
